import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Button, Row, Col, Alert, Card, CardBody, Badge } from 'reactstrap';
import { H6 } from '../../../../AbstractElements';

const rewardValuePresets = {
  topup: { topup: 10000, description: '' },
  points: { points: 1000, description: '' },
  badge: { badgeName: '', badgeIcon: '🏆', description: '' },
  voucher: { voucherCode: '', description: '' },
  gift: { giftName: '', giftValue: 0, description: '' },
  coupon: { couponCode: '', discountValue: 0, description: '' },
  discount: { discountPercent: 10, maxDiscount: 50000, description: '' },
  freeship: { maxValue: 30000, minOrder: 0, description: '' }
};

const normalizeReward = (reward, displayQuestions) => {
  const safeTotal = Math.max(1, Number(displayQuestions) || 1);
  const rewardType = reward.rewardType || 'topup';
  const preset = rewardValuePresets[rewardType] || { description: '' };
  const rawValue = typeof reward.rewardValue === 'object' && reward.rewardValue !== null ? reward.rewardValue : {};
  const normalizedValue = { ...preset, ...rawValue };

  return {
    ...reward,
    rewardType,
    totalQuestions: Number.isFinite(reward.totalQuestions) ? reward.totalQuestions : safeTotal,
    correctAnswers: Number.isFinite(reward.correctAnswers)
      ? Math.min(safeTotal, Math.max(0, reward.correctAnswers))
      : Math.min(safeTotal, rewardType === 'badge' ? 0 : safeTotal),
    rewardValue: normalizedValue,
    quantity: Number.isFinite(reward.quantity) ? Math.max(0, reward.quantity) : 1,
    sortOrder: reward.sortOrder || 1,
    isActive: reward.isActive ?? true
  };
};

const buildDefaultRewards = (displayQuestions) => {
  const total = Math.max(1, Number(displayQuestions) || 1);
  const defaults = [
    {
      totalQuestions: total,
      correctAnswers: total,
      message: 'tặng TOPUP 10K từ RVOPV',
      rewardType: 'topup',
      rewardValue: { topup: 10000, description: 'Xuất sắc! Bạn đã chinh phục toàn bộ câu hỏi 🎉' },
      quantity: 1,
      sortOrder: 1
    },
    {
      totalQuestions: total,
      correctAnswers: Math.max(1, Math.floor(total * 0.7)),
      message: 'tặng TOPUP 10K từ RVOPV',
      rewardType: 'topup',
      rewardValue: { topup: 10000, description: 'Gần đạt điểm tuyệt đối rồi! 💎' },
      quantity: 1,
      sortOrder: 2
    },
    {
      totalQuestions: total,
      correctAnswers: Math.max(1, Math.floor(total * 0.5)),
      message: 'tặng TOPUP 10K từ RVOPV',
      rewardType: 'topup',
      rewardValue: { topup: 10000, description: 'Khởi đầu tốt! 💰' },
      quantity: 1,
      sortOrder: 3
    },
    {
      totalQuestions: total,
      correctAnswers: 0,
      message: 'Đừng nản lòng! Hãy thử lại sau! 💪',
      rewardType: 'badge',
      rewardValue: { badgeName: 'Người mới bắt đầu', badgeIcon: '🌱', description: '' },
      quantity: 1,
      sortOrder: 4
    }
  ];

  return defaults.map((reward) => normalizeReward(reward, total));
};

const CreateQuizModal = ({ isOpen, toggle, formData, setFormData, onSubmit, isSubmitting }) => {
  const [previewUrl, setPreviewUrl] = useState(formData.thumbnailUrl || null);
  const [forceRender, setForceRender] = useState(0);
  const [createRewards, setCreateRewards] = useState(false); // Mặc định true khi tạo mới
  
  // Default rewards template based on displayQuestions
  const [rewardsData, setRewardsData] = useState(() => buildDefaultRewards(formData.displayQuestions));
  
  const [questions, setQuestions] = useState(() => {
    // Transform API data structure to form structure
    if (formData.questions && formData.questions.length > 0) {
      return formData.questions.map((q, index) => {
        return {
          content: typeof q.content === 'string' ? q.content : q.content?.text || '',
          type: q.type,
          timeLimit: q.timeLimit ?? undefined, // Không set mặc định 30 khi load
          mediaFile: null,
          mediaPreview: q.mediaUrl || null,
          answers: q.answers?.map((a, aIndex) => {
            return {
              text: a.content || a.text || '',
              isCorrect: a.isCorrect
            };
          }) || []
        };
      });
    }
    return [];
  });
  
  const [questionMediaFiles, setQuestionMediaFiles] = useState([]);
  const [timeLimitErrors, setTimeLimitErrors] = useState({});

  // Reset modal state whenever it is opened
  useEffect(() => {
    if (!isOpen) {
      setTimeLimitErrors({});
      return;
    }

    setPreviewUrl(formData.thumbnailUrl || null);
    setTimeLimitErrors({});

    const total = Math.max(1, Number(formData.displayQuestions) || 1);
    const initialRewards =
      formData.rewardsData && formData.rewardsData.length > 0
        ? formData.rewardsData.map((reward) => normalizeReward(reward, total))
        : buildDefaultRewards(total);

    setRewardsData(initialRewards);
    setCreateRewards(false);
  }, [isOpen]);

  // Update previewUrl when formData changes
  useEffect(() => {
    if (formData.thumbnailUrl) {
      setPreviewUrl(formData.thumbnailUrl);
    }
  }, [formData.thumbnailUrl]);

  // Force re-render when questions change
  useEffect(() => {
    setForceRender(prev => prev + 1);
  }, [questions]);

  // Update questions when formData changes
  useEffect(() => {
    if (formData.questions && formData.questions.length > 0) {
      const transformedQuestions = formData.questions.map((q, index) => {
        return {
          content: typeof q.content === 'string' ? q.content : q.content?.text || '',
          type: q.type,
          timeLimit: q.timeLimit ?? undefined, // Không set mặc định 30 khi transform
          mediaFile: null,
          mediaPreview: q.mediaUrl || null,
          answers: q.answers?.map(a => {
            return {
              text: a.content || a.text || '',
              isCorrect: a.isCorrect
            };
          }) || []
        };
      });
      setQuestions(transformedQuestions);
    }
  }, [formData.questions]);

  useEffect(() => {
    if (!isOpen || !createRewards) return;

    const total = Math.max(1, Number(formData.displayQuestions) || 1);
    setRewardsData((prev) => {
      if (!prev || prev.length === 0) {
        return buildDefaultRewards(total);
      }

      return prev.map((reward) =>
        normalizeReward(
          {
            ...reward,
            totalQuestions: total,
            correctAnswers: Math.min(
              total,
              Number.isFinite(reward.correctAnswers) ? reward.correctAnswers : total
            )
          },
          total
        )
      );
    });
  }, [formData.displayQuestions, createRewards, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    if (createRewards) {
      const syncedRewards = rewardsData.map((reward) =>
        normalizeReward(
          {
            ...reward,
            totalQuestions: formData.displayQuestions || reward.totalQuestions
          },
          formData.displayQuestions
        )
      );

      setFormData((prev) => ({
        ...prev,
        createRewards: true,
        rewardsData: syncedRewards
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        createRewards: false,
        rewardsData: []
      }));
    }
  }, [createRewards, rewardsData, isOpen, formData.displayQuestions, setFormData]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Vui lòng chọn file hình ảnh');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        alert('File size không được vượt quá 5MB');
        return;
      }

      setFormData({ ...formData, thumbnail: file });

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleQuestionMediaChange = (qIndex, file) => {
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Vui lòng chọn file hình ảnh');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        alert('File size không được vượt quá 5MB');
        return;
      }

      const newQuestions = [...questions];
      newQuestions[qIndex].mediaFile = file;
      
      const reader = new FileReader();
      reader.onloadend = () => {
        newQuestions[qIndex].mediaPreview = reader.result;
        setQuestions([...newQuestions]);
      };
      reader.readAsDataURL(file);
      
      // Update questionMediaFiles array
      const newMediaFiles = [...questionMediaFiles];
      newMediaFiles[qIndex] = file;
      setQuestionMediaFiles(newMediaFiles);
      setFormData({ ...formData, questionMedias: newMediaFiles });
    }
  };

  const handleAddQuestion = () => {
    const newQuestion = {
      content: '',
      type: 'single',
      timeLimit: 30,
      mediaFile: null,
      mediaPreview: null,
      answers: [
        { text: '', isCorrect: true },
        { text: '', isCorrect: false }
      ]
    };
    const newQuestions = [...questions, newQuestion];
    setQuestions(newQuestions);
    setFormData({ ...formData, questions: newQuestions });
  };

  const handleRemoveQuestion = (qIndex) => {
    const newQuestions = questions.filter((_, idx) => idx !== qIndex);
    const newMediaFiles = questionMediaFiles.filter((_, idx) => idx !== qIndex);
    setQuestions(newQuestions);
    setQuestionMediaFiles(newMediaFiles);
    setFormData({ ...formData, questions: newQuestions, questionMedias: newMediaFiles });
  };

  const handleQuestionChange = (qIndex, field, value) => {
    const newQuestions = [...questions];
    newQuestions[qIndex][field] = value;
    setQuestions(newQuestions);
    setFormData({ ...formData, questions: newQuestions });
  };

  const handleAddAnswer = (qIndex) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].answers.push({ text: '', isCorrect: false });
    setQuestions(newQuestions);
    setFormData({ ...formData, questions: newQuestions });
  };

  const handleRemoveAnswer = (qIndex, aIndex) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].answers = newQuestions[qIndex].answers.filter((_, idx) => idx !== aIndex);
    setQuestions(newQuestions);
    setFormData({ ...formData, questions: newQuestions });
  };

  const handleAnswerChange = (qIndex, aIndex, field, value) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].answers[aIndex][field] = value;
    
    // For single choice, ensure only one correct answer
    if (field === 'isCorrect' && value && newQuestions[qIndex].type === 'single') {
      newQuestions[qIndex].answers.forEach((answer, idx) => {
        if (idx !== aIndex) answer.isCorrect = false;
      });
    }
    
    setQuestions(newQuestions);
    setFormData({ ...formData, questions: newQuestions });
  };

  // Add new reward
  const handleAddReward = () => {
    const newReward = {
      totalQuestions: formData.displayQuestions,
      correctAnswers: Math.floor(formData.displayQuestions * 0.8),
      message: 'Chúc mừng! Bạn đạt thành tích tốt!',
      rewardType: 'topup',
      rewardValue: { topup: 5000, description: 'Phần thưởng tùy chỉnh' },
      quantity: 1,
      sortOrder: rewardsData.length + 1
    };
    setRewardsData([...rewardsData, normalizeReward(newReward, formData.displayQuestions)]);
  };

  // Remove reward
  const handleRemoveReward = (index) => {
    const newRewards = rewardsData.filter((_, idx) => idx !== index);
    // Update sort order
    newRewards.forEach((r, idx) => {
      r.sortOrder = idx + 1;
    });
    setRewardsData(newRewards);
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="xl"
      style={{ maxWidth: '90%' }}
      centered
      backdrop="static"
      keyboard={!isSubmitting}
      modalTransition={{ timeout: 200 }}
      backdropTransition={{ timeout: 150 }}
    >
      <ModalHeader toggle={!isSubmitting ? toggle : undefined}>
        Tạo Quiz Template Mới
      </ModalHeader>
      <Form onSubmit={onSubmit}>
        <ModalBody style={{maxHeight: '70vh', overflowY: 'auto'}}>
          <Row>
            {/* Basic Info */}
            <Col md="12">
              <Card className="mb-3">
                <CardBody>
                  <H6 className="mb-3">Thông tin cơ bản</H6>
                  <Row>
                    <Col md="12">
                      <FormGroup>
                        <Label>Tên Quiz Template *</Label>
                        <Input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          placeholder="Quiz Kiến Thức Y Dược"
                          required
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>Thumbnail *</Label>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          required
                        />
                        <small className="text-muted">Max 5MB. JPG, PNG, GIF, WebP</small>
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>Số câu hỏi hiển thị *</Label>
                        <Input
                          type="number"
                          value={formData.displayQuestions}
                          onChange={(e) => setFormData({...formData, displayQuestions: parseInt(e.target.value)})}
                          min="1"
                          required
                        />
                        <small className="text-muted">Random từ tổng số câu hỏi</small>
                      </FormGroup>
                    </Col>
                    {previewUrl && (
                      <Col md="12">
                        <div className="border rounded p-2">
                          <img 
                            src={previewUrl} 
                            alt="Thumbnail" 
                            className="img-fluid rounded"
                            style={{maxHeight: '200px', width: '100%', objectFit: 'contain'}}
                          />
                        </div>
                      </Col>
                    )}
                    <Col md="12">
                      <div className="form-check form-switch">
                        <Input
                          type="checkbox"
                          className="form-check-input"
                          checked={formData.isActive}
                          onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                        />
                        <Label className="form-check-label">
                          {formData.isActive ? 'Active' : 'Inactive'}
                        </Label>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>

            {/* Questions */}
            <Col md="12">
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <H6 className="mb-0">Câu hỏi ({questions.length})</H6>
                    <Button color="success" size="sm" onClick={handleAddQuestion}>
                      <i className="fa fa-plus me-1"></i>
                      Thêm câu hỏi
                    </Button>
                  </div>

                  {questions.map((question, qIndex) => (
                    <Card key={qIndex} className="mb-3 border">
                      <CardBody className="p-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <Badge color="primary">Câu {qIndex + 1}</Badge>
                          <Button 
                            color="danger" 
                            size="sm"
                            onClick={() => handleRemoveQuestion(qIndex)}
                          >
                            <i className="fa fa-trash"></i>
                          </Button>
                        </div>

                        <Row>
                          <Col md="12">
                            <FormGroup>
                              <Label>Nội dung câu hỏi *</Label>
                              <Input
                                type="textarea"
                                rows="2"
                                value={question.content}
                                onChange={(e) => handleQuestionChange(qIndex, 'content', e.target.value)}
                                placeholder="Nhập nội dung câu hỏi..."
                                required
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label>Loại câu hỏi</Label>
                              <Input
                                type="select"
                                value={question.type}
                                onChange={(e) => handleQuestionChange(qIndex, 'type', e.target.value)}
                              >
                                <option value="single">Single Choice (1 đáp án đúng)</option>
                                <option value="multi">Multiple Choice (nhiều đáp án đúng)</option>
                              </Input>
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label>Thời gian giới hạn (giây) *</Label>
                              <Input
                                type="number"
                                value={question.timeLimit ?? ''}
                                invalid={!!timeLimitErrors[qIndex]}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  // Xóa lỗi khi bắt đầu nhập
                                  if (timeLimitErrors[qIndex]) {
                                    setTimeLimitErrors(prev => {
                                      const newErrors = { ...prev };
                                      delete newErrors[qIndex];
                                      return newErrors;
                                    });
                                  }
                                  
                                  if (val === '') {
                                    handleQuestionChange(qIndex, 'timeLimit', undefined);
                                  } else {
                                    const num = parseInt(val);
                                    if (!isNaN(num) && num > 0) {
                                      handleQuestionChange(qIndex, 'timeLimit', num);
                                    }
                                  }
                                }}
                                onBlur={(e) => {
                                  const val = e.target.value;
                                  if (!val || val === '' || !question.timeLimit || question.timeLimit < 1 || question.timeLimit > 300) {
                                    setTimeLimitErrors(prev => ({
                                      ...prev,
                                      [qIndex]: 'Vui lòng nhập thời gian giới hạn từ 1 đến 300 giây'
                                    }));
                                    // BỎ phần tự động set về 30 - để người dùng tự nhập
                                  } else {
                                    // Xóa lỗi nếu hợp lệ
                                    setTimeLimitErrors(prev => {
                                      const newErrors = { ...prev };
                                      delete newErrors[qIndex];
                                      return newErrors;
                                    });
                                  }
                                }}
                                min="1"
                                max="300"
                                required
                              />
                              {timeLimitErrors[qIndex] && (
                                <small className="text-danger d-block mt-1">
                                  <i className="fa fa-exclamation-circle me-1"></i>
                                  {timeLimitErrors[qIndex]}
                                </small>
                              )}
                              {!timeLimitErrors[qIndex] && (
                                <small className="text-muted">Thời gian tối đa để trả lời câu hỏi này (giây)</small>
                              )}
                            </FormGroup>
                          </Col>
                          <Col md="12">
                            <FormGroup>
                              <Label>Hình ảnh câu hỏi (tùy chọn)</Label>
                              <Input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleQuestionMediaChange(qIndex, e.target.files[0])}
                              />
                              <small className="text-muted">Max 5MB. JPG, PNG, GIF, WebP</small>
                            </FormGroup>
                          </Col>
                          {question.mediaPreview && (
                            <Col md="12">
                              <div className="border rounded p-2 mb-2">
                                <img 
                                  src={question.mediaPreview} 
                                  alt={`Question ${qIndex + 1} media`}
                                  className="img-fluid rounded"
                                  style={{maxHeight: '200px', width: '100%', objectFit: 'contain'}}
                                />
                              </div>
                            </Col>
                          )}
                          <Col md="12">
                            <Label className="d-flex justify-content-between align-items-center">
                              <span>Đáp án</span>
                              <Button 
                                color="success" 
                                size="sm"
                                onClick={() => handleAddAnswer(qIndex)}
                              >
                                <i className="fa fa-plus me-1"></i>
                                Thêm đáp án
                              </Button>
                            </Label>
                            {question.answers.map((answer, aIndex) => {
                              return (
                                <div key={`${qIndex}-${aIndex}`} className="d-flex gap-2 mb-2">
                                  <div className="form-check d-flex align-items-center">
                                    {question.type === 'single' ? (
                                      <Input
                                        key={`${qIndex}-${aIndex}-${forceRender}`}
                                        type="radio"
                                        className="form-check-input"
                                        checked={Boolean(answer.isCorrect)}
                                        onChange={(e) => {
                                          handleAnswerChange(qIndex, aIndex, 'isCorrect', e.target.checked);
                                        }}
                                        name={`question-${qIndex}-correct`}
                                        id={`question-${qIndex}-answer-${aIndex}`}
                                        style={{transform: 'scale(1.2)'}}
                                      />
                                    ) : (
                                      <div 
                                        className="form-check-input"
                                        style={{
                                          width: '20px',
                                          height: '20px',
                                          border: '2px solid #007bff',
                                          borderRadius: '3px',
                                          backgroundColor: answer.isCorrect ? '#007bff' : 'white',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          transform: 'scale(1.2)'
                                        }}
                                        onClick={() => {
                                          handleAnswerChange(qIndex, aIndex, 'isCorrect', !answer.isCorrect);
                                        }}
                                      >
                                        {answer.isCorrect && (
                                          <i className="fa fa-check text-white" style={{fontSize: '12px'}}></i>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                <Input
                                  type="text"
                                  value={answer.text}
                                  onChange={(e) => handleAnswerChange(qIndex, aIndex, 'text', e.target.value)}
                                  placeholder={`Đáp án ${String.fromCharCode(65 + aIndex)}`}
                                  required
                                />
                                <Button 
                                  color="danger" 
                                  size="sm"
                                  onClick={() => handleRemoveAnswer(qIndex, aIndex)}
                                  disabled={question.answers.length <= 2}
                                >
                                  <i className="fa fa-trash"></i>
                                </Button>
                                </div>
                              );
                            })}
                            <small className="text-muted">
                              {question.type === 'single' 
                                ? 'Chọn 1 đáp án đúng' 
                                : 'Có thể chọn nhiều đáp án đúng'}
                            </small>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  ))}

                  {questions.length === 0 && (
                    <Alert color="info">
                      <i className="fa fa-info-circle me-2"></i>
                      Chưa có câu hỏi nào. Nhấn "Thêm câu hỏi" để bắt đầu.
                    </Alert>
                  )}
                </CardBody>
              </Card>
            </Col>

            {/* Rewards Section */}
            <Col md="12">
                <Card className="border-warning">
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div className="d-flex align-items-center">
                        <i className="fa fa-gift text-warning me-2" style={{fontSize: '1.5rem'}}></i>
                        <H6 className="mb-0">Phần thưởng (Optional)</H6>
                      </div>
                      <div className="form-check form-switch">
                        <Input
                          type="checkbox"
                          className="form-check-input"
                          id="createRewardsToggle"
                          checked={createRewards}
                          onChange={(e) => setCreateRewards(e.target.checked)}
                        />
                        <Label className="form-check-label" htmlFor="createRewardsToggle">
                          Tạo phần thưởng tự động
                        </Label>
                      </div>
                    </div>

                    {createRewards ? (
                      <>
                        <Alert color="info" className="mb-3">
                          <i className="fa fa-info-circle me-1"></i>
                          <small>
                            Hệ thống tạo sẵn <strong>4 mức phần thưởng</strong> mặc định. 
                            Bạn có thể chỉnh sửa hoặc thêm mức mới.
                          </small>
                        </Alert>

                        <div className="d-flex justify-content-end mb-3">
                          <Button color="success" size="sm" onClick={handleAddReward}>
                            <i className="fa fa-plus me-1"></i>
                            Thêm mức thưởng
                          </Button>
                        </div>

                        <Row>
                          {rewardsData.map((reward, index) => {
                            const badgeColor = index === 0 ? 'success' : 
                                             index === 1 ? 'primary' : 
                                             index === 2 ? 'warning' : 
                                             index === 3 ? 'secondary' : 'info';
                            return (
                            <Col md="6" key={index}>
                              <Card className={`mb-3 border-${badgeColor}`}>
                                <CardBody className="p-3">
                                  <div className="d-flex justify-content-between align-items-start mb-2">
                                    <div className="d-flex align-items-center gap-2">
                                      <Badge color={badgeColor}>
                                        Mức {index + 1}
                                      </Badge>
                                      <small className="text-muted">
                                        ({reward.correctAnswers}/{reward.totalQuestions || formData.displayQuestions} câu)
                                      </small>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                      <small className="text-muted">Thứ tự: {reward.sortOrder}</small>
                                      <Button 
                                        color="danger" 
                                        size="sm"
                                        onClick={() => handleRemoveReward(index)}
                                        disabled={rewardsData.length <= 1}
                                        title="Xóa reward"
                                      >
                                        <i className="fa fa-trash" style={{fontSize: '10px'}}></i>
                                      </Button>
                                    </div>
                                  </div>

                                  <Row>
                                    <Col md="6">
                                      <FormGroup>
                                        <Label className="small">Số câu đúng</Label>
                                        <Input
                                          type="number"
                                          size="sm"
                                          value={reward.correctAnswers}
                                          onChange={(e) => {
                                            const newRewards = [...rewardsData];
                                            newRewards[index].correctAnswers = parseInt(e.target.value);
                                            setRewardsData(newRewards);
                                          }}
                                          min="0"
                                          max={formData.displayQuestions}
                                        />
                                        <small className="text-muted">/ {formData.displayQuestions} câu</small>
                                      </FormGroup>
                                    </Col>
                                    <Col md="6">
                                      <FormGroup>
                                        <Label className="small">Tổng số câu</Label>
                                        <Input
                                          type="number"
                                          size="sm"
                                          value={reward.totalQuestions || formData.displayQuestions}
                                          onChange={(e) => {
                                            const newRewards = [...rewardsData];
                                            newRewards[index].totalQuestions = parseInt(e.target.value);
                                            setRewardsData(newRewards);
                                          }}
                                          disabled
                                        />
                                        <small className="text-muted">Auto-sync</small>
                                      </FormGroup>
                                    </Col>
                                  </Row>

                                  <FormGroup>
                                    <Label className="small">Message</Label>
                                    <Input
                                      type="textarea"
                                      size="sm"
                                      rows="2"
                                      value={reward.message}
                                      onChange={(e) => {
                                        const newRewards = [...rewardsData];
                                        newRewards[index].message = e.target.value;
                                        setRewardsData(newRewards);
                                      }}
                                    />
                                  </FormGroup>

                                  <Row>
                                    <Col md="6">
                                      <FormGroup>
                                        <Label className="small">Loại thưởng</Label>
                                        <Input
                                          type="select"
                                          size="sm"
                                          value={reward.rewardType}
                                          onChange={(e) => {
                                            const newRewards = [...rewardsData];
                                            newRewards[index].rewardType = e.target.value;
                                            // Reset rewardValue when type changes
                                            const rewardValueDefaults = {
                                              topup: { topup: 10000, description: '' },
                                              points: { points: 1000, description: '' },
                                              badge: { badgeName: '', badgeIcon: '🏆' },
                                              voucher: { voucherCode: '', description: '' },
                                              gift: { giftName: '', giftValue: 0, description: '' },
                                              coupon: { couponCode: '', discountValue: 0, description: '' },
                                              discount: { discountPercent: 10, maxDiscount: 50000, description: '' },
                                              freeship: { maxValue: 30000, minOrder: 0, description: '' }
                                            };
                                            
                                            newRewards[index].rewardValue = rewardValueDefaults[e.target.value] || { description: '' };
                                            setRewardsData(newRewards);
                                          }}
                                        >
                                          <option value="topup">💰 Topup (Nạp tiền điện thoại)</option>
                                          <option value="points">⭐ Points (Điểm tích lũy)</option>
                                          <option value="badge">🏆 Badge (Huy hiệu)</option>
                                          <option value="voucher">🎫 Voucher (Phiếu giảm giá)</option>
                                          <option value="gift">🎁 Gift (Quà tặng)</option>
                                          <option value="coupon">🎟️ Coupon (Mã giảm giá)</option>
                                          <option value="discount">💸 Discount (Giảm giá %)</option>
                                          <option value="freeship">🚚 Freeship (Miễn phí ship)</option>
                                        </Input>
                                      </FormGroup>
                                    </Col>
                                    <Col md="6">
                                      <FormGroup>
                                        <Label className="small">
                                          {reward.rewardType === 'topup' ? 'Số tiền topup (VNĐ)' :
                                           reward.rewardType === 'points' ? 'Số điểm' : 
                                           reward.rewardType === 'badge' ? 'Tên Badge' : 
                                           reward.rewardType === 'voucher' ? 'Mã Voucher' :
                                           reward.rewardType === 'gift' ? 'Tên quà tặng' :
                                           reward.rewardType === 'coupon' ? 'Mã Coupon' :
                                           reward.rewardType === 'discount' ? 'Phần trăm giảm (%)' :
                                           reward.rewardType === 'freeship' ? 'Giá trị tối đa' : 'Giá trị'}
                                        </Label>
                                        <Input
                                          type={
                                            reward.rewardType === 'topup' || 
                                            reward.rewardType === 'points' || 
                                            reward.rewardType === 'discount' ? 'number' : 'text'
                                          }
                                          size="sm"
                                          value={
                                            reward.rewardType === 'topup' ? (reward.rewardValue.topup || '') :
                                            reward.rewardType === 'points' ? (reward.rewardValue.points || '') :
                                            reward.rewardType === 'badge' ? (reward.rewardValue.badgeName || '') :
                                            reward.rewardType === 'voucher' ? (reward.rewardValue.voucherCode || '') :
                                            reward.rewardType === 'gift' ? (reward.rewardValue.giftName || '') :
                                            reward.rewardType === 'coupon' ? (reward.rewardValue.couponCode || '') :
                                            reward.rewardType === 'discount' ? (reward.rewardValue.discountPercent || '') :
                                            reward.rewardType === 'freeship' ? (reward.rewardValue.maxValue || '') : ''
                                          }
                                          onChange={(e) => {
                                            const newRewards = [...rewardsData];
                                            const value = (reward.rewardType === 'topup' || 
                                                          reward.rewardType === 'points' || 
                                                          reward.rewardType === 'discount')
                                              ? parseInt(e.target.value) || 0
                                              : e.target.value;
                                            
                                            if (reward.rewardType === 'topup') {
                                              newRewards[index].rewardValue.topup = value;
                                            } else if (reward.rewardType === 'points') {
                                              newRewards[index].rewardValue.points = value;
                                            } else if (reward.rewardType === 'badge') {
                                              newRewards[index].rewardValue.badgeName = value;
                                            } else if (reward.rewardType === 'voucher') {
                                              newRewards[index].rewardValue.voucherCode = value;
                                            } else if (reward.rewardType === 'gift') {
                                              newRewards[index].rewardValue.giftName = value;
                                            } else if (reward.rewardType === 'coupon') {
                                              newRewards[index].rewardValue.couponCode = value;
                                            } else if (reward.rewardType === 'discount') {
                                              newRewards[index].rewardValue.discountPercent = value;
                                            } else if (reward.rewardType === 'freeship') {
                                              newRewards[index].rewardValue.maxValue = value;
                                            }
                                            setRewardsData(newRewards);
                                          }}
                                          placeholder={
                                            reward.rewardType === 'topup' ? '10000' :
                                            reward.rewardType === 'points' ? '1000' : 
                                            reward.rewardType === 'badge' ? 'Gold Badge' :
                                            reward.rewardType === 'voucher' ? 'VOUCHER123' :
                                            reward.rewardType === 'gift' ? 'Túi quà tặng' :
                                            reward.rewardType === 'coupon' ? 'COUPON50' :
                                            reward.rewardType === 'discount' ? '50' :
                                            reward.rewardType === 'freeship' ? '100000' : 'Nhập giá trị'
                                          }
                                        />
                                      </FormGroup>
                                    </Col>
                                  </Row>
                                  
                                  <FormGroup>
                                    <Label className="small">Mô tả phần thưởng</Label>
                                    <Input
                                      type="text"
                                      size="sm"
                                      value={reward.rewardValue.description || ''}
                                      onChange={(e) => {
                                        const newRewards = [...rewardsData];
                                        newRewards[index].rewardValue.description = e.target.value;
                                        setRewardsData(newRewards);
                                      }}
                                      placeholder="Xuất sắc! Bạn đã chinh phục toàn bộ câu hỏi 🎉"
                                    />
                                  </FormGroup>

                                  <FormGroup>
                                    <Label className="small">Số lượng (Quantity) *</Label>
                                    <Input
                                      type="number"
                                      size="sm"
                                      value={reward.quantity || 1}
                                      onChange={(e) => {
                                        const newRewards = [...rewardsData];
                                        newRewards[index].quantity = Math.max(0, parseInt(e.target.value) || 1);
                                        setRewardsData(newRewards);
                                      }}
                                      min="0"
                                      required
                                    />
                                    <small className="text-muted">Số lượng phần thưởng có thể trao cho user</small>
                                  </FormGroup>
                                </CardBody>
                              </Card>
                            </Col>
                          );
                          })}
                        </Row>
                      </>
                    ) : (
                      <>
                        <Alert color="danger" className="mb-3">
                          <div className="d-flex align-items-start">
                            <i className="fa fa-exclamation-triangle me-2 mt-1" style={{fontSize: '1.2rem'}}></i>
                            <div>
                              <strong>⚠️ CẢNH BÁO:</strong> Quiz không có phần thưởng sẽ KHÔNG hoạt động trên client!
                              <br />
                              <small className="text-muted">
                                Mobile app yêu cầu quiz phải có rewards để hiển thị kết quả và trao thưởng cho user.
                              </small>
                            </div>
                          </div>
                        </Alert>
                        <Alert color="light" className="mb-0">
                          <i className="fa fa-hand-point-up me-2"></i>
                          <strong>Vui lòng bật công tắc phía trên để tạo phần thưởng!</strong>
                          <br />
                          <small>Hệ thống sẽ tự động tạo 4 mức thưởng phù hợp cho quiz của bạn.</small>
                        </Alert>
                      </>
                    )}
                  </CardBody>
                </Card>
              </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle} disabled={isSubmitting}>
            Hủy
          </Button>
          <Button color="primary" type="submit" disabled={isSubmitting || questions.length === 0}>
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Đang xử lý...
              </>
            ) : (
              <>
                <i className="fa fa-save me-2"></i>
                {createRewards ? 'Tạo Quiz + Rewards' : 'Tạo Quiz'}
              </>
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CreateQuizModal;

